import { useEffect, useMemo, useState } from 'react';
import { isTeamPosition, playerImageSources, playerInitials } from '../services/playerImages.js';

/**
 * One player's face, wherever a player is named.
 *
 * It degrades instead of breaking. The image sources come back best-first —
 * headshot, then team logo — and an `onError` walks to the next one, so a player
 * Sleeper has no photo of ends up in his team's colours and a player on no team
 * ends up as initials. Nothing here can leave a broken-image glyph on the board.
 *
 * **It is decorative, deliberately.** Every avatar in this app sits directly
 * beside the player's own name, so `alt` is empty and the wrapper is
 * `aria-hidden`: a screen reader that read the picture too would announce every
 * player twice. The name next to it is the accessible label, and it is already
 * there.
 *
 * `pending` draws the empty ring and nothing in it. The draft board needs that:
 * its ids arrive a moment after its rows, and without it 120 rows would paint
 * initials or a team logo and then visibly flip to headshots. The ring holds the
 * space so the swap is the picture arriving, not the layout moving.
 */
const SIZES = new Set(['xs', 'sm', 'md', 'lg']);

const PlayerAvatar = ({
  name,
  team,
  position,
  sleeperId,
  size = 'sm',
  className = '',
  pending = false,
}) => {
  const sources = useMemo(
    () => playerImageSources({ sleeperId, team, position }),
    [sleeperId, team, position],
  );

  // The identity of the chain, not its length — a row that swaps one player for
  // another has to start again at the headshot rather than inherit the previous
  // player's failures. Sorting by rank re-uses these components across players.
  const chainKey = sources.join('|');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => setAttempt(0), [chainKey]);

  const src = sources[attempt] || null;
  const sizeClass = SIZES.has(size) ? size : 'sm';
  const isTeam = isTeamPosition(position);

  // Empty ring while the ids are still in flight, the image once there is one,
  // initials once the chain is spent.
  let inner = null;
  if (!pending) {
    inner = src ? (
      <img
        className="ff-avatar__img"
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        // Third-party CDN: send no referrer and claim no credentials.
        referrerPolicy="no-referrer"
        onError={() => setAttempt((current) => current + 1)}
      />
    ) : (
      <span className="ff-avatar__initials">{playerInitials(name)}</span>
    );
  }

  return (
    <span
      className={`ff-avatar ff-avatar--${sizeClass} ${isTeam ? 'ff-avatar--team' : ''} ${className}`}
      aria-hidden="true"
    >
      {inner}
    </span>
  );
};

export default PlayerAvatar;
